import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Trash2, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UnassignedEvents = ({ unassignedEvents, rooms, onRefresh }) => {
  const [assigningEvent, setAssigningEvent] = useState(null);
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [loading, setLoading] = useState(false);

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return format(date, 'd MMMM yyyy', { locale: pl });
    } catch {
      return dateStr;
    }
  };

  const handleAssignClick = (event) => {
    setAssigningEvent(event);
    if (rooms.length > 0) {
      setSelectedRoomId(rooms[0].id);
    }
  };

  const handleAssignConfirm = async () => {
    if (!selectedRoomId) {
      toast.error('Wybierz salę');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const response = await axios.post(
        `${API}/unassigned-events/${assigningEvent.id}/assign`,
        { room_id: selectedRoomId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Impreza przypisana do ${response.data.room}`);
      setAssigningEvent(null);
      onRefresh();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Błąd przypisywania');
      } else {
        toast.error('Wystąpił błąd podczas przypisywania imprezy');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę imprezę?')) return;

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/unassigned-events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Impreza usunięta');
      onRefresh();
    } catch (error) {
      toast.error('Błąd podczas usuwania imprezy');
    }
  };

  if (unassignedEvents.length === 0) {
    return (
      <Card className="bg-white/80 backdrop-blur-sm border-purple-200 shadow-lg">
        <CardHeader>
          <CardTitle className="text-2xl" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Imprezy do przypisania
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center py-8 text-gray-500">Brak imprez oczekujących na przypisanie</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-white/80 backdrop-blur-sm border-purple-200 shadow-lg">
        <CardHeader>
          <CardTitle className="text-2xl" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Imprezy do przypisania
            <span className="ml-3 text-lg font-normal text-gray-500">({unassignedEvents.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {unassignedEvents.map((event) => (
              <div
                key={event.id}
                className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-orange-200 hover:shadow-md transition-shadow"
                data-testid={`unassigned-event-${event.id}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      {event.name} {event.surname}
                    </h3>
                    <span className="px-3 py-1 rounded-full text-sm font-semibold bg-orange-200 text-orange-800">
                      Nieprzypisana
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <CalendarIcon className="w-4 h-4" />
                      {formatDate(event.date)}
                    </span>
                    <span className="flex items-center gap-1 font-semibold">
                      <Clock className="w-4 h-4" />
                      {event.start_time} - {event.end_time}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleAssignClick(event)}
                    className="text-white font-semibold flex items-center gap-2"
                    style={{ background: '#6E35C0' }}
                    data-testid={`assign-event-${event.id}`}
                  >
                    <UserPlus className="w-4 h-4" />
                    Przypisz
                  </Button>
                  <Button
                    onClick={() => handleDelete(event.id)}
                    variant="ghost"
                    size="sm"
                    className="hover:bg-red-100 hover:text-red-600"
                    data-testid={`delete-unassigned-${event.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Assign Dialog */}
      {assigningEvent && (
        <Dialog open={true} onOpenChange={() => setAssigningEvent(null)}>
          <DialogContent className="sm:max-w-[450px]" data-testid="assign-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Przypisz imprezę do sali
              </DialogTitle>
              <DialogDescription>
                {assigningEvent.name} {assigningEvent.surname} - {formatDate(assigningEvent.date)}, {assigningEvent.start_time} - {assigningEvent.end_time}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Wybierz salę</label>
                <Select value={selectedRoomId} onValueChange={setSelectedRoomId}>
                  <SelectTrigger className="h-11" data-testid="room-select-assign">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {rooms.map(room => (
                      <SelectItem key={room.id} value={room.id}>{room.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button
                variant="outline"
                onClick={() => setAssigningEvent(null)}
                disabled={loading}
              >
                Anuluj
              </Button>
              <Button
                onClick={handleAssignConfirm}
                disabled={loading}
                className="text-white"
                style={{ background: '#6E35C0' }}
                data-testid="confirm-assign-button"
              >
                {loading ? 'Przypisywanie...' : 'Przypisz'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default UnassignedEvents;
